/**
 * CelShapes — the authored shape vocabulary for ability VFX.
 *
 * Every shape here is drawn the CelLook way: an opaque-looking body in the
 * identity colour, a white-hot core, hard cel bands and an INK rim, dying by
 * noise erosion with a bright front. Alpha-blended, LDR: it reads on the bright
 * deck with bloom off. Shapes are drawn, not blurred — polar stars, crescents,
 * thick bands, spikes — because a soft hoop reads as decoration and a crisp
 * filled shape reads as an impact.
 *
 * Everything is a node material (WebGPU crashes legacy ones), and every
 * per-instance value (tint, progress, erosion, fade) rides a UNIFORM, never a
 * TSL const — a const bakes its value into the shader and every distinct value
 * becomes its own program compile (the ParticleUtils lesson).
 *
 * Builders return `{ mesh, u }` where `u` holds the uniforms the caller
 * animates. Geometry is shared out of a module cache — callers must NOT
 * dispose it. Call releaseCelShape(mesh) on expiry/clear; a complete bundle
 * returns to the pool. Never directly dispose a pooled material.
 */

import * as THREE from 'three/webgpu';
import { BoundedResourcePool } from './BoundedResourcePool.js';
import {
  Fn,
  uv,
  float,
  vec2,
  vec3,
  smoothstep,
  step,
  uniform,
  mix,
  atan,
  fract,
  time,
  sin,
  cos,
  max,
  pow,
} from 'three/tsl';
import { cloud, erode, palette, coreGain, identity, floorTint } from './CelLook.js';

// ─── Shared geometry (never disposed by callers) ────────────────

let _circleGeo = null;
let _planeGeo = null;
let _hexPrismGeo = null;

function circleGeo() {
  if (!_circleGeo) _circleGeo = new THREE.CircleGeometry(1, 64);
  return _circleGeo;
}
function planeGeo() {
  if (!_planeGeo) _planeGeo = new THREE.PlaneGeometry(1, 1);
  return _planeGeo;
}
function hexPrismGeo() {
  if (!_hexPrismGeo) _hexPrismGeo = new THREE.CylinderGeometry(1, 1, 1, 6, 1, true);
  return _hexPrismGeo;
}

// ─── Node helpers ───────────────────────────────────────────────

/** 1 inside [a,b] with a hard-but-antialiased edge. Cel bands are made of these. */
const band = (x, a, b, aa = 0.02) =>
  smoothstep(float(a - aa), float(a + aa), x).mul(smoothstep(float(b + aa), float(b - aa), x));

/** The common uniforms: tint, fade (a multiplier), erode (0 whole → 1 gone), gain, seed. */
function commonUniforms(colorHex) {
  return {
    tint: uniform(new THREE.Color(colorHex)),
    fade: uniform(1),
    erode: uniform(0),
    gain: uniform(1),
    seed: uniform(Math.random() * 10),
  };
}

/** The palette, with the per-shape gain stacked on the tier's core gain. */
function ramp(u) {
  return palette(u.tint, coreGain.mul(u.gain));
}

function baseMaterial({ additive = false, depthTest = true } = {}) {
  const mat = new THREE.MeshBasicNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  mat.depthTest = depthTest;
  mat.side = THREE.DoubleSide;
  if (additive) mat.blending = THREE.AdditiveBlending;
  return mat;
}

function flatOnGround(mesh, x, y, z, yaw = 0) {
  mesh.rotation.x = -Math.PI / 2;
  mesh.rotation.z = yaw;
  mesh.position.set(x, y, z);
  mesh.frustumCulled = false;
  return mesh;
}

// ─── Builders ───────────────────────────────────────────────────

/**
 * The shock band: a THICK filled ring at the ability's exact radius — the
 * splash footprint itself, not a hoop around it. Ink on both edges, the body
 * colour, a hot band and a white keyline through the middle; it erodes away
 * in blotches. Unit mesh; the caller animates `mesh.scale` out to the radius.
 * `bandStart` (the `inner` uniform) sets the band's thickness as a fraction.
 */
function createBurstRing(colorHex, { bandStart = 0.6 } = {}) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const inner = uniform(bandStart);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const q = r.sub(inner).div(float(1).sub(inner));
  const n = cloud(local.mul(3.2), u.seed.mul(13));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.12 });
  mat.colorNode = Fn(() => {
    // Wide ink on both edges: a band this pale (cream, amber) needs the ink to
    // read as a drawn shape on the deck.
    let col = c.ink;
    col = mix(col, c.body, band(q, 0.14, 0.86, 0.03));
    col = mix(col, c.hot, band(q, 0.34, 0.68, 0.03));
    col = mix(col, c.core, band(q, 0.46, 0.56, 0.025));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => band(q, 0.0, 1.0, 0.02).mul(e.alive).mul(u.fade))();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, inner } };
}

/**
 * The melee swing: a filled CRESCENT smear — thick through the middle of the
 * sweep, tapering to points at both ends — with a white leading edge that
 * WIPES across the sector as `progress` runs 0→1, then erodes. The sector's
 * range and half-angle are the ability's own numbers.
 */
function createSlashArc(range, halfAngleDeg, colorHex) {
  const half = (halfAngleDeg * Math.PI) / 180;
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const progress = uniform(0);
  const halfU = uniform(half);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const ang = atan(local.x, local.y); // 0 = the quad's +Y = caster forward
  const a01 = ang.div(halfU); // -1..1 across the sector
  // Crescent: the inner edge bows toward the rim at both ends of the sweep.
  const innerR = float(0.3).add(a01.mul(a01).mul(0.5));
  const q = r.sub(innerR).div(float(1).sub(innerR)); // 0 inner edge → 1 rim
  const lead = mix(float(-1.18), float(1.18), progress).mul(halfU);
  const n = cloud(local.mul(2.6), u.seed.mul(7));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.12 });

  mat.colorNode = Fn(() => {
    let col = c.body;
    col = mix(col, c.hot, band(q, 0.3, 0.78, 0.04));
    // Speed streaks through the body.
    const spoke = step(float(0.6), fract(ang.mul(11.0).add(u.seed)));
    col = mix(col, c.hot, spoke.mul(0.25).mul(band(q, 0.15, 0.85, 0.05)));
    // Ink on both edges of the crescent.
    col = mix(col, c.ink, band(q, 0.9, 1.0, 0.03));
    col = mix(col, c.ink, band(q, 0.0, 0.09, 0.03));
    // The leading edge burns white across the whole thickness.
    col = mix(col, c.core, band(ang.sub(lead), -0.26, 0.02, 0.04));
    col = mix(col, c.core, e.front);
    return col;
  })();

  mat.opacityNode = Fn(() => {
    const inCone = step(ang.abs(), halfU).mul(band(q, 0.0, 1.0, 0.02));
    // Revealed behind the wipe, with a hard front; the trailing end thins out.
    const revealed = smoothstep(lead.add(0.03), lead.sub(0.05), ang);
    return inCone.mul(revealed).mul(e.alive).mul(u.fade);
  })();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.scale.setScalar(range);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, progress, half: halfU } };
}

/**
 * The impact burst: a SPIKY six-point star — three long spikes and three
 * short ones between them, every spike a different length per instance so no
 * two hits are the same stamp — the saturated body colour, a white heart only
 * at the very centre, an ink rim. The first version was an eight-petal
 * flower: round lobes, a hot band out to half the radius, and a white core
 * that sat behind the white damage number. A Sprite so it always faces the
 * match camera: an impact must read from 55° overhead, where a flat ground
 * star would foreshorten to a line.
 */
function createStarFlash(colorHex, { additive = false } = {}) {
  const mat = new THREE.SpriteNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  if (additive) mat.blending = THREE.AdditiveBlending;
  const u = commonUniforms(colorHex);
  const spin = uniform(Math.random() * Math.PI * 2);
  mat.rotationNode = spin;
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const ang = atan(local.x, local.y);
  const a3 = ang.mul(3).add(u.seed);
  // Long spikes on a 120° pattern, short ones 60° off; each spike's length
  // drifts with a low-frequency wobble seeded per instance (asymmetry).
  const longLen = float(0.78).add(sin(ang.add(u.seed.mul(3.1))).mul(0.14)).add(sin(ang.mul(2).add(u.seed)).mul(0.06));
  const shortLen = float(0.42).add(sin(ang.mul(2).sub(u.seed.mul(2.3))).mul(0.12));
  const longP = pow(a3.cos().abs(), 14).mul(longLen);
  const shortP = pow(a3.sin().abs(), 22).mul(shortLen);
  const R = float(0.1).add(longP).add(shortP);
  const q = r.div(R);
  const n = cloud(local.mul(2.4), u.seed.mul(11));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.14 });

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, band(q, 0.0, 0.88, 0.035));
    col = mix(col, c.hot, band(q, 0.0, 0.3, 0.05).mul(smoothstep(float(0.5), float(0.2), r)));
    col = mix(col, c.core, smoothstep(float(0.17), float(0.1), r));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => smoothstep(float(1.0), float(0.96), q).mul(e.alive).mul(u.fade))();

  const sprite = new THREE.Sprite(mat);
  sprite.frustumCulled = false;
  return { mesh: sprite, u: { ...u, spin } };
}

/**
 * Ground cracks: jagged ink fractures radiating from a slam, drawn flat at
 * the slam's radius. Seven spokes with noise-jittered paths, thick at the
 * centre and thinning to nothing at the rim, that erode away from the
 * outside in. The tank's signature needs the DECK to look hit.
 */
function createCracks(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const a01 = atan(local.x, local.y).mul(1 / (Math.PI * 2)).add(0.5);
  // Each spoke wanders with the radius; the wander is per-instance noise.
  const wander = cloud(vec2(r.mul(3.5), u.seed), u.seed.mul(4)).sub(0.5).mul(0.09);
  const spoke = fract(a01.mul(7).add(wander).add(u.seed)).sub(0.5).abs();
  // Angular half-width in spoke units: thick near the centre, gone at the rim.
  const w = float(0.16).mul(float(1).sub(r)).mul(float(1).sub(r)).add(0.012);
  const line = smoothstep(w.add(0.012), w, spoke);
  // Branches: a second, finer set that only lives in the middle third.
  const twig = smoothstep(float(0.04), float(0.02), fract(a01.mul(13).sub(wander.mul(2)).add(u.seed.mul(1.7))).sub(0.5).abs())
    .mul(smoothstep(float(0.15), float(0.3), r)).mul(smoothstep(float(0.75), float(0.5), r));
  const crack = line.max(twig.mul(0.8)).mul(smoothstep(float(1.0), float(0.85), r)).mul(smoothstep(float(0.02), float(0.1), r));
  const n = cloud(local.mul(3), u.seed.mul(9));
  // Erodes from the rim inward: the outer cracks close first.
  const prog = u.erode.mul(1.2).add(r.mul(0.35).mul(u.erode)).clamp(0, 1);
  const e = erode(n, prog, { edge: 0.05, front: 0.1 });

  mat.colorNode = Fn(() => mix(c.ink, c.hot, e.front.mul(0.7)))();
  mat.opacityNode = Fn(() => crack.mul(e.alive).mul(u.fade).mul(0.92))();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

/**
 * The muzzle flash: a spike star pointing down the barrel — one long lobe
 * forward, two side lobes, a stub behind. A Sprite, turned to the aim on
 * screen by `spin`, so it keeps its full area from the 55° camera. White
 * down the axis, hot, body, ink rim; erodes out in 90 ms.
 */
function createMuzzleFlash(colorHex) {
  const mat = new THREE.SpriteNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  const u = commonUniforms(colorHex);
  const spin = uniform(0);
  mat.rotationNode = spin;
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const ang = atan(local.x, local.y); // 0 = +y = forward
  const fwd = pow(max(cos(ang), float(0)), 4).mul(0.76);
  const sideA = pow(max(cos(ang.sub(0.8)), float(0)), 12).mul(0.46);
  const sideB = pow(max(cos(ang.add(0.8)), float(0)), 12).mul(0.46);
  const back = pow(max(cos(ang).negate(), float(0)), 3).mul(0.2);
  const jag = sin(ang.mul(7).add(u.seed.mul(5))).mul(0.03);
  const R = float(0.22).add(fwd).add(sideA).add(sideB).add(back).add(jag);
  const q = r.div(R);
  const n = cloud(local.mul(2.0), u.seed.mul(9));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.14 });

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, band(q, 0.0, 0.86, 0.04));
    col = mix(col, c.hot, band(q, 0.0, 0.52, 0.05));
    // The white axis: the core runs the length of the forward lobe.
    const axis = smoothstep(float(0.12), float(0.04), local.x.abs()).mul(step(float(0), local.y));
    col = mix(col, c.core, band(q, 0.0, 0.26, 0.05).max(axis.mul(band(q, 0.0, 0.78, 0.05))));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => smoothstep(float(1.0), float(0.95), q).mul(e.alive).mul(u.fade))();

  const sprite = new THREE.Sprite(mat);
  sprite.frustumCulled = false;
  return { mesh: sprite, u: { ...u, spin } };
}

/**
 * A speed smear: a long teardrop streak along +y (the head at +y), white
 * centre line, body, ink edges, eroding from the tail. Dashes and afterimages.
 */
function createSmear(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  // Teardrop: wide near the head (+y), pointed at the tail.
  const wY = float(0.36).mul(smoothstep(float(-1.0), float(0.4), local.y));
  const dd = local.x.abs().div(wY.max(0.02));
  const inside = smoothstep(float(1.0), float(0.92), dd).mul(smoothstep(float(1.0), float(0.96), local.y.abs()));
  const ink = smoothstep(float(0.8), float(0.9), dd);
  const n = cloud(local.mul(vec2(3, 1.4)), u.seed.mul(6));
  // Erodes from the tail forward as `erode` rises: the tail is a little ahead
  // of the head, never gone before the erosion has started.
  const prog = u.erode.mul(1.3).add(local.y.negate().add(1).mul(0.5).mul(0.25).mul(u.erode.add(0.2))).clamp(0, 1);
  const e = erode(n, prog, { edge: 0.05, front: 0.12 });
  mat.colorNode = Fn(() => {
    let col = mix(c.body, c.hot, smoothstep(float(0.55), float(0.25), dd));
    col = mix(col, c.core, smoothstep(float(0.28), float(0.08), dd).mul(smoothstep(float(-0.6), float(0.3), local.y)));
    col = mix(col, c.ink, ink);
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => inside.mul(e.alive).mul(u.fade))();

  const mesh = new THREE.Mesh(planeGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

/**
 * A lobbed shell: a round cartoon bomb with an ink rim, a lit side and a hot
 * nose highlight, spinning slowly. A Sprite: it reads from every angle.
 */
function createShell(colorHex) {
  const mat = new THREE.SpriteNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  const u = commonUniforms(colorHex);
  const spin = uniform(0);
  mat.rotationNode = spin;
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const mask = smoothstep(float(0.98), float(0.92), r);
  const ink = smoothstep(float(0.78), float(0.86), r);
  const lit = smoothstep(float(0.9), float(0.1), local.sub(vec2(-0.3, 0.35)).length());
  const spec = smoothstep(float(0.34), float(0.12), local.sub(vec2(-0.32, 0.38)).length());
  mat.colorNode = Fn(() => {
    let col = mix(c.deep, c.body, lit);
    col = mix(col, c.hot, spec.mul(0.9));
    col = mix(col, c.core, smoothstep(float(0.18), float(0.06), local.sub(vec2(-0.34, 0.4)).length()));
    col = mix(col, c.ink, ink);
    return col;
  })();
  mat.opacityNode = Fn(() => mask.mul(u.fade))();
  const sprite = new THREE.Sprite(mat);
  sprite.frustumCulled = false;
  return { mesh: sprite, u: { ...u, spin } };
}

/**
 * The hexagonal self ring (self_aura.jpg) — hexagonal ON PURPOSE, so a buff
 * never reads as a ground zone at a glance. Flat at the feet; the caller
 * scales it. Ink outside, body, a white keyline.
 */
function createHexRing(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  // Pointy-top hex distance.
  const hexD = local.y.abs().max(local.x.abs().mul(0.866).add(local.y.abs().mul(0.5)));
  const n = cloud(local.mul(3), u.seed.mul(5));
  const e = erode(n, u.erode);

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, band(hexD, 0.62, 0.9, 0.03));
    col = mix(col, c.core, band(hexD, 0.72, 0.8, 0.025));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => band(hexD, 0.56, 0.97, 0.02).mul(e.alive).mul(u.fade))();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

/**
 * The line strike: a ground band with a white core line, body, ink edges — and
 * a pulse head that travels 0→1 down the length as `progress` runs. Geometry is
 * (width × length), the ability's own numbers; laid flat and yawed by the caller.
 * Erodes in blotches when it is done.
 */
function createLineWall(lengthUnits, width, colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const progress = uniform(0);
  const c = ramp(u);

  const along = uv().y;
  const across = uv().x.sub(0.5).abs().mul(2);
  const n = cloud(uv().mul(vec2(3, 9)), u.seed.mul(4));
  const e = erode(n, u.erode);

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, band(across, 0.0, 0.84, 0.04));
    col = mix(col, c.hot, band(across, 0.0, 0.5, 0.04));
    col = mix(col, c.core, band(across, 0.0, 0.2, 0.04));
    // Cracks: dark fractures across the body.
    const crack = step(float(0.82), fract(along.mul(9).add(sin(across.mul(6).add(u.seed)).mul(0.3))));
    col = mix(col, c.ink, crack.mul(band(across, 0.25, 0.84, 0.04)).mul(0.8));
    // The travelling head burns white across the whole width.
    col = mix(col, c.core, band(along.sub(progress), -0.1, 0.01, 0.03));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => {
    const revealed = smoothstep(progress.add(0.015), progress.sub(0.05), along);
    return revealed.mul(smoothstep(float(1.0), float(0.95), across)).mul(e.alive).mul(u.fade);
  })();

  const mesh = new THREE.Mesh(planeGeo(), mat);
  mesh.scale.set(width, lengthUnits, 1);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, progress } };
}

/**
 * The zone duration marker (ground_zone.jpg): a solid outer band, a rotating
 * ring of glyph ticks, a thin inner lip — hollow middle. This is the "still
 * dangerous" indicator for placed areas, drawn at the authored radius for the
 * authored durationMs. Glyphs rotate via `time`, costing zero CPU per frame.
 */
function createZoneRing(colorHex, { radius = 3 } = {}) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  // Band widths are authored for a ~3m zone. Deindexed is 10m and Starlink
  // 12m — proportional bands there become metre-thick hoops that wall off the
  // whole fight (seen, not guessed: two ults filled the mockup screen).
  // Compress the bands toward the outer edge so WORLD thickness stays put.
  const k = uniform(Math.min(1, Math.max(0.28, 3.2 / radius)));
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  // Remap so band coords live in the outer `k` fraction of the radius.
  const r = float(1).sub(float(1).sub(local.length()).div(k));
  const a01 = atan(local.x, local.y).mul(1 / (Math.PI * 2)).add(0.5);
  const ticks = step(float(0.4), fract(a01.mul(20).add(time.mul(0.12))));
  const n = cloud(local.mul(4), u.seed.mul(3));
  const e = erode(n, u.erode);

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, band(r, 0.9, 0.985, 0.015)); // solid outer band
    col = mix(col, c.core, band(r, 0.925, 0.95, 0.012)); // white keyline
    col = mix(col, c.body, band(r, 0.7, 0.85, 0.015).mul(ticks)); // glyph ticks
    col = mix(col, c.hot, band(r, 0.6, 0.64, 0.012)); // inner lip
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => {
    const outer = band(r, 0.88, 1.0, 0.015);
    const glyphs = band(r, 0.7, 0.85, 0.015).mul(ticks).mul(0.7);
    const lip = band(r, 0.59, 0.65, 0.012).mul(0.6);
    return outer.add(glyphs).add(lip).min(1).mul(e.alive).mul(u.fade).mul(0.72);
  })();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, thickness: k } };
}

/** A triangle wave in -1..1 of `x` (period 1). The bolt's zigzag is two of these. */
const tri = (x) => fract(x).mul(2).sub(1).abs().mul(2).sub(1);

/**
 * A jagged bolt between two points — Chain Lightning's link, and the
 * electric ticks off an overclocked body. A THICK cel bolt: a white core down
 * a zigzag (two triangle waves, seeded per instance, pinned to both ends so
 * it meets what it joins), a hot band, the body colour and an INK outline —
 * alpha-blended, so it reads on the bright deck. A fork peels off the main
 * bolt a third of the way along and thins to nothing. Pass 2's version was a
 * thin additive ribbon with no rim that vanished over a pale floor.
 */
function createBoltBeam(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const along = uv().y;
  const x = uv().x.sub(0.5).mul(2);
  // Both ends pinned: the wave amplitude dies at along 0 and 1.
  const pin = sin(along.mul(Math.PI)).pow(0.6);
  const zig = tri(along.mul(5.5).add(u.seed)).mul(0.3).add(tri(along.mul(13).add(u.seed.mul(1.7))).mul(0.12)).mul(pin);
  const dist = x.sub(zig).abs();
  // The fork: branches off at a seeded point, runs for 30% of the length and
  // thins out; its side is the seed's parity.
  const a0 = float(0.28).add(fract(u.seed.mul(0.37)).mul(0.3));
  const side = fract(u.seed.mul(0.61)).sub(0.5).sign();
  const k = along.sub(a0).div(0.3);
  const forkAlive = step(float(0), k).mul(step(k, float(1)));
  const forkZig = zig.add(k.mul(0.9).mul(side)).add(tri(along.mul(11).add(u.seed)).mul(0.08));
  const forkDist = x.sub(forkZig).abs().add(float(1).sub(forkAlive).mul(9));
  const forkW = float(1).sub(k).clamp(0, 1).mul(0.55);
  // Signed thickness of whichever bolt is nearest, in across units.
  const d = dist.min(forkDist.div(forkW.max(0.05)));

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, smoothstep(float(0.4), float(0.34), d));
    col = mix(col, c.hot, smoothstep(float(0.26), float(0.2), d));
    col = mix(col, c.core, smoothstep(float(0.13), float(0.07), d));
    return col;
  })();
  mat.opacityNode = Fn(() => smoothstep(float(0.5), float(0.44), d).mul(u.fade))();

  const mesh = new THREE.Mesh(planeGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

export { flatOnGround, tri };

// Keys describe every shader/geometry variant. Colour, angles, band widths
// and dimensions are uniforms or transforms (including half/thickness), NOT
// compile-time constants. They are reset below so arbitrary roster values
// cannot create an unbounded key cache. Pass 3 opened the pool to the status,
// zone and telegraph shapes (StatusShapes, ZoneShapes, Telegraphs) so every
// shape in the game is acquired and released the same way and covered by
// the same prewarm.
const _pool = new BoundedResourcePool({
  maxKeys: 40, maxFreePerKey: 14, maxFree: 240,
  dispose: ({ mesh }) => mesh.material.dispose(), // geometry belongs to cache / THREE.Sprite
});
const _resources = new WeakMap();

/**
 * `floor` shapes are painted on the deck in a `telegraphHex` and keep that
 * colour as authored (`floorTint`); everything else is an IDENTITY shape and
 * takes the vivid form of the character colour (`identity`) — the same
 * transform the particles, ribbons and tracers use, so a hit star, a swing
 * and the shards off it are one colour. Before this the shapes took the raw
 * hex and AAPL's graphite star came out plain white.
 */
export function acquireShape(key, create, colorHex, { additive = false, floor = false, fixed = false, depthTest = true } = {}) {
  const resource = _pool.acquire(key, create);
  const { mesh, u } = resource;
  _resources.set(mesh, resource);
  mesh.removeFromParent();
  mesh.position.set(0, 0, 0);
  mesh.rotation.set(0, 0, 0, 'XYZ');
  mesh.scale.set(1, 1, 1);
  mesh.matrix.identity();
  mesh.matrixWorld.identity();
  mesh.matrixAutoUpdate = true;
  mesh.matrixWorldAutoUpdate = true;
  mesh.matrixWorldNeedsUpdate = true;
  mesh.visible = true;
  mesh.frustumCulled = false;
  mesh.renderOrder = 0;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.layers.set(0);
  mesh.name = '';
  mesh.userData = { sharedGeo: true, noOutline: true };
  if (mesh.isSprite) mesh.center.set(0.5, 0.5);
  const mat = mesh.material;
  mat.visible = true;
  mat.opacity = 1;
  mat.transparent = true;
  mat.depthWrite = false;
  mat.depthTest = depthTest;
  mat.colorWrite = true;
  mat.side = mesh.isSprite ? THREE.FrontSide : THREE.DoubleSide;
  mat.blending = additive ? THREE.AdditiveBlending : THREE.NormalBlending;
  mat.color.set(0xffffff);
  // `fixed` is a colour that MEANS something whoever wears it (stun amber,
  // slow ice): taken as authored, never pushed through identity().
  if (fixed) u.tint.value.set(colorHex);
  else if (floor) floorTint(colorHex, u.tint.value);
  else identity(colorHex, u.tint.value);
  u.fade.value = 1;
  if (u.erode) u.erode.value = 0;
  if (u.progress) u.progress.value = 0;
  if (u.gain) u.gain.value = 1;
  if (u.spin) u.spin.value = Math.random() * Math.PI * 2;
  if (u.seed) u.seed.value = Math.random() * 10;
  if (u.hit) u.hit.value = 0;
  if (u.armed) u.armed.value = 0;
  if (u.pulse) u.pulse.value = 0;
  return resource;
}

/** The shared building blocks, for the shape modules that extend this vocabulary. */
export { commonUniforms, ramp, band, baseMaterial, circleGeo, planeGeo, hexPrismGeo };

/** True means this pool owns the mesh, including an already released mesh.
 * This lets generic expiry paths avoid falling back to disposal on duplicates.
 * Like other pools, callers must relinquish references after releasing a lease. */
export function releaseCelShape(mesh) {
  const resource = _resources.get(mesh);
  if (!resource) return false;
  // Release may dispose overflow; detachment must happen before that disposal.
  mesh.removeFromParent();
  _pool.release(resource);
  return true;
}

/** Pool live includes the core bundles retained by ShaderPrewarm once
 * its optional queue finishes. Gameplay live/anchor counts are separate. */
export const getCelShapeStats = () => _pool.stats();

export function burstRing(colorHex, { bandStart = 0.6 } = {}) {
  const r = acquireShape('burstRing', () => createBurstRing(colorHex), colorHex, { floor: true });
  r.u.inner.value = bandStart;
  return r;
}
export function slashArc(range, halfAngleDeg, colorHex) {
  const r = acquireShape('slashArc', () => createSlashArc(range, halfAngleDeg, colorHex), colorHex);
  r.u.half.value = halfAngleDeg * Math.PI / 180;
  r.mesh.scale.setScalar(range);
  return r;
}
export function starFlash(colorHex, { additive = false } = {}) {
  return acquireShape(`starFlash:${additive ? 'additive' : 'normal'}`,
    () => createStarFlash(colorHex, { additive }), colorHex, { additive });
}
export function muzzleFlash(colorHex) {
  const r = acquireShape('muzzleFlash', () => createMuzzleFlash(colorHex), colorHex);
  r.u.spin.value = 0;
  return r;
}
export function smear(colorHex) {
  return acquireShape('smear', () => createSmear(colorHex), colorHex);
}
export function shell(colorHex) {
  const r = acquireShape('shell', () => createShell(colorHex), colorHex);
  r.u.spin.value = 0;
  return r;
}
export function cracks(colorHex) {
  return acquireShape('cracks', () => createCracks(colorHex), colorHex);
}
export function hexRing(colorHex) {
  return acquireShape('hexRing', () => createHexRing(colorHex), colorHex, { floor: true });
}
export function lineWall(lengthUnits, width, colorHex) {
  const r = acquireShape('lineWall', () => createLineWall(lengthUnits, width, colorHex), colorHex, { floor: true });
  r.mesh.scale.set(width, lengthUnits, 1);
  return r;
}
export function zoneRing(colorHex, { radius = 3 } = {}) {
  const r = acquireShape('zoneRing', () => createZoneRing(colorHex), colorHex, { floor: true });
  r.u.thickness.value = Math.min(1, Math.max(0.28, 3.2 / radius));
  return r;
}
export function boltBeam(colorHex) {
  return acquireShape('boltBeam', () => createBoltBeam(colorHex), colorHex);
}
